import { useRef, useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { selectCurrentLanguage } from 'services/slices/auth'

type SpeechSynthesisStatus = null | 'speaking' | 'paused' | 'stopped' | 'error'
type SpeechSynthesisOptions = Partial<
  Pick<SpeechSynthesisUtterance, 'lang' | 'pitch' | 'rate' | 'volume' | 'voice'>
>

export function useTextToSpeech(options?: SpeechSynthesisOptions) {
  const language = useSelector(selectCurrentLanguage) || 'en'

  const [status, setStatus] = useState<SpeechSynthesisStatus>(null)
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null)

  useEffect(() => {
    if (!utteranceRef.current) {
      utteranceRef.current = new SpeechSynthesisUtterance()
      utteranceRef.current.onerror = (event) => {
        console.error('Speech synthesis error:', event.error)
        setStatus('error')
      }
    }

    const utterance = utteranceRef.current
    utterance.lang = options?.lang || language
    utterance.pitch = options?.pitch || 1
    utterance.rate = options?.rate || 1
    utterance.volume = options?.volume || 0.5

    return () => {
      if (speechSynthesis && utterance) {
        speechSynthesis.cancel()
      }
    }
  }, [options, language])

  const speak = (message: string) => {
    setStatus('speaking')
    if (utteranceRef.current) {
      utteranceRef.current.text = message
      speechSynthesis.speak(utteranceRef.current)
    }
  }

  const pause = () => {
    speechSynthesis.pause()
    setStatus('paused')
  }

  const stop = () => {
    speechSynthesis.cancel()
    setStatus('stopped')
  }

  return { speak, pause, stop, status }
}
